import * as Headless from "@headlessui/react";
import clsx from "clsx";
import React from "react";
import { Link, LinkProps } from "./link";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { Text } from "./text";

export const styles = {
    base: [
        // Base
        "relative isolate inline-flex items-baseline justify-center gap-x-2 rounded-lg border text-base/6 font-semibold",
        // Sizing
        "px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6",
        // Focus
        "focus:outline-hidden data-focus:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500",
        // Disabled
        "data-disabled:opacity-50",
        // Icon
        "*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]",
    ],
    sizes: {
        sm: "px-[calc(--spacing(2.5)-1px)] py-[calc(--spacing(1.5)-1px)] text-sm/5 sm:text-xs/5",
        md: "px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] text-base/6 sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6",
        lg: "px-[calc(--spacing(5)-1px)] py-[calc(--spacing(3)-1px)] text-base/6 sm:px-[calc(--spacing(4)-1px)] sm:py-[calc(--spacing(2.5)-1px)] sm:text-base/6",
    },
    solid: [
        // Optical border, implemented as the button background to avoid corner artifacts
        "border-transparent bg-(--btn-border)",
        // Dark mode: border is rendered on `after` so background is set to button background
        "dark:bg-(--btn-bg)",
        // Button background, implemented as foreground layer to stack on top of pseudo-border layer
        "before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg)-1px)] before:bg-(--btn-bg)",
        // Drop shadow, applied to the inset `before` layer so it blends with the border
        "before:shadow-sm",
        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
        "dark:before:hidden",
        // Dark mode: Subtle white outline is applied using a border
        "dark:border-white/5",
        // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
        "after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]",
        // Inner highlight shadow
        "after:shadow-[shadow:inset_0_1px_--theme(--color-white/15%)]",
        // White overlay on hover
        "data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)",
        // Dark mode: `after` layer expands to cover entire button
        "dark:after:-inset-px dark:after:rounded-lg",
        // Disabled
        "data-disabled:before:shadow-none data-disabled:after:shadow-none",
    ],
    outline: [
        // Base
        "border-zinc-950/10 text-zinc-950 data-active:bg-zinc-950/[2.5%] data-hover:bg-zinc-950/[2.5%]",
        // Dark mode
        "dark:border-white/15 dark:text-white dark:[--btn-bg:transparent] dark:data-active:bg-white/5 dark:data-hover:bg-white/5",
        // Icon
        "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
    ],
    plain: [
        // Base
        "border-transparent text-zinc-950 data-active:bg-zinc-950/5 data-hover:bg-zinc-950/5",
        // Dark mode
        "dark:text-white dark:data-active:bg-white/10 dark:data-hover:bg-white/10",
        // Icon
        "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
    ],
    colors: {
        "dark/zinc": [
            "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
            "dark:text-white dark:[--btn-bg:var(--color-zinc-600)] dark:[--btn-hover-overlay:var(--color-white)]/5",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
        ],
        light: [
            "text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/[2.5%] data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15",
            "dark:text-white dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]",
            "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
        ],
        "dark/white": [
            "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
            "dark:text-zinc-950 dark:[--btn-bg:white] dark:[--btn-hover-overlay:var(--color-zinc-950)]/5",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
        ],
        dark: [
            "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
            "dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
        ],
        white: [
            "text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/[2.5%] data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15",
            "dark:[--btn-hover-overlay:var(--color-zinc-950)]/5",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-500)] data-hover:[--btn-icon:var(--color-zinc-500)]",
        ],
        zinc: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-zinc-600)] [--btn-border:var(--color-zinc-700)]/90",
            "dark:[--btn-hover-overlay:var(--color-white)]/5",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
        ],
        indigo: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-indigo-500)] [--btn-border:var(--color-indigo-600)]/90",
            "[--btn-icon:var(--color-indigo-300)] data-active:[--btn-icon:var(--color-indigo-200)] data-hover:[--btn-icon:var(--color-indigo-200)]",
        ],
        cyan: [
            "text-cyan-950 [--btn-bg:var(--color-cyan-300)] [--btn-border:var(--color-cyan-400)]/80 [--btn-hover-overlay:var(--color-white)]/25",
            "[--btn-icon:var(--color-cyan-500)]",
        ],
        red: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-red-600)] [--btn-border:var(--color-red-700)]/90",
            "[--btn-icon:var(--color-red-300)] data-active:[--btn-icon:var(--color-red-200)] data-hover:[--btn-icon:var(--color-red-200)]",
        ],
        orange: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-orange-500)] [--btn-border:var(--color-orange-600)]/90",
            "[--btn-icon:var(--color-orange-300)] data-active:[--btn-icon:var(--color-orange-200)] data-hover:[--btn-icon:var(--color-orange-200)]",
        ],
        amber: [
            "text-amber-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-amber-400)] [--btn-border:var(--color-amber-500)]/80",
            "[--btn-icon:var(--color-amber-600)]",
        ],
        yellow: [
            "text-yellow-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-yellow-300)] [--btn-border:var(--color-yellow-400)]/80",
            "[--btn-icon:var(--color-yellow-600)] data-active:[--btn-icon:var(--color-yellow-700)] data-hover:[--btn-icon:var(--color-yellow-700)]",
        ],
        lime: [
            "text-lime-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-lime-300)] [--btn-border:var(--color-lime-400)]/80",
            "[--btn-icon:var(--color-lime-600)] data-active:[--btn-icon:var(--color-lime-700)] data-hover:[--btn-icon:var(--color-lime-700)]",
        ],
        green: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-green-600)] [--btn-border:var(--color-green-700)]/90",
            "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80",
        ],
        emerald: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-emerald-600)] [--btn-border:var(--color-emerald-700)]/90",
            "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80",
        ],
        teal: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-teal-600)] [--btn-border:var(--color-teal-700)]/90",
            "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80",
        ],
        sky: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-sky-500)] [--btn-border:var(--color-sky-600)]/80",
            "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80",
        ],
        blue: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-blue-600)] [--btn-border:var(--color-blue-700)]/90",
            "[--btn-icon:var(--color-blue-400)] data-active:[--btn-icon:var(--color-blue-300)] data-hover:[--btn-icon:var(--color-blue-300)]",
        ],
        violet: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-violet-500)] [--btn-border:var(--color-violet-600)]/90",
            "[--btn-icon:var(--color-violet-300)] data-active:[--btn-icon:var(--color-violet-200)] data-hover:[--btn-icon:var(--color-violet-200)]",
        ],
        purple: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-purple-500)] [--btn-border:var(--color-purple-600)]/90",
            "[--btn-icon:var(--color-purple-300)] data-active:[--btn-icon:var(--color-purple-200)] data-hover:[--btn-icon:var(--color-purple-200)]",
        ],
        fuchsia: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-fuchsia-500)] [--btn-border:var(--color-fuchsia-600)]/90",
            "[--btn-icon:var(--color-fuchsia-300)] data-active:[--btn-icon:var(--color-fuchsia-200)] data-hover:[--btn-icon:var(--color-fuchsia-200)]",
        ],
        pink: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-pink-500)] [--btn-border:var(--color-pink-600)]/90",
            "[--btn-icon:var(--color-pink-300)] data-active:[--btn-icon:var(--color-pink-200)] data-hover:[--btn-icon:var(--color-pink-200)]",
        ],
        rose: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-rose-500)] [--btn-border:var(--color-rose-600)]/90",
            "[--btn-icon:var(--color-rose-300)] data-active:[--btn-icon:var(--color-rose-200)] data-hover:[--btn-icon:var(--color-rose-200)]",
        ],
    },
};

/** Controls padding and font size of a {@link Button}, and the dimension of its loading spinner. */
export type ButtonSize = keyof typeof styles.sizes;

/** Props for the internal {@link Spinner} component. */
type SpinnerProps = {
    /** Controls the spinner's dimensions — matches the enclosing button size. */
    size: ButtonSize;
};

/** Animated SVG spinner rendered inside a {@link Button} when `loading` is true. */
function Spinner(props: SpinnerProps) {
    const { size } = props;
    const dim = size === "lg" ? "size-5" : size === "sm" ? "size-3.5" : "size-4";
    return (
        <svg data-slot="icon" className={clsx(dim, "animate-spin")} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
            <path d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
    );
}

/**
 * The properties for {@link Button}
 */
export type ButtonProps = (
    | {
          /** The button color variant */
          color?: keyof typeof styles.colors;
          /** Not available with color */
          outline?: never;
          /** Not available with color */
          plain?: never;
      }
    | {
          /** Not available with outline */
          color?: never;
          /** Whether to use an outline style */
          outline: true;
          /** Not available with outline */
          plain?: never;
      }
    | {
          /** Not available with plain */
          color?: never;
          /** Not available with plain */
          outline?: never;
          /** Whether to use a plain/ghost style */
          plain: true;
      }
    | {
          /** The button color variant - Runtime variable variant */
          color?: keyof typeof styles.colors;
          /** Whether to use an outline style - Runtime variable variant */
          outline?: boolean;
          /** Whether to use a plain/ghost style - Runtime variable variant */
          plain?: boolean;
      }
) & {
    /** Additional CSS classes */
    className?: string;
    /** The button content */
    children: React.ReactNode;
    /** Button size — also controls the loading spinner dimension */
    size?: ButtonSize;
    /** Shows a spinner and disables the button while true */
    loading?: boolean;
} & (
        | Omit<Headless.ButtonProps, "className">
        | Omit<LinkProps, "className" | "render">
        | (Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "href"> & {
              /** Whether this is an external link */
              external: true;
              /** The external URL */
              href: string;
          })
    );

/**
 * A versatile button that renders as a button, router link, or external link.
 *
 * @example
 * ```tsx
 * <Button>Save changes</Button>
 * <Button color="cyan">Colored</Button>
 * <Button outline>Outline</Button>
 * <Button plain>Plain</Button>
 * <Button href="/get-started">As link</Button>
 * ```
 *
 * @param props - The component props
 * @param ref - The forwarded ref
 * @returns The rendered button element
 * @see https://catalyst.tailwindui.com/docs/button
 */
export const Button = React.forwardRef(function Button(props: ButtonProps, ref: React.ForwardedRef<HTMLElement>) {
    const { color, outline, plain, className, children, size = "md", loading, ...rest } = props;
    const classes = clsx(
        className,
        styles.base,
        styles.sizes[size],
        outline ? styles.outline : plain ? styles.plain : clsx(styles.solid, styles.colors[color ?? "dark/zinc"]),
    );
    const content = (
        <TouchTarget>
            {loading ? <Spinner size={size} /> : null}
            {children}
        </TouchTarget>
    );

    if ("href" in rest) {
        if ("external" in rest) {
            const { external, ...other } = rest;
            const _external = external;
            return (
                <a {...other} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
                    {content}
                </a>
            );
        } else {
            return (
                <Link {...rest} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
                    {content}
                </Link>
            );
        }
    } else {
        return (
            <Headless.Button
                {...rest}
                disabled={loading || rest.disabled}
                className={clsx(classes, "cursor-default")}
                ref={ref}
            >
                {content}
            </Headless.Button>
        );
    }
});

/**
 * The properties for {@link TouchTarget}
 */
export type TouchTargetProps = {
    /** The child elements */
    children: React.ReactNode;
};

/**
 * Expands the hit area to at least 44x44px on touch devices
 */
export function TouchTarget(props: TouchTargetProps) {
    return (
        <>
            <span
                className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
                aria-hidden="true"
            />
            {props.children}
        </>
    );
}

/**
 * The properties for {@link BackButton}
 */
export type BackButtonProps = ButtonProps & {
    /** Not available for BackButton */
    color?: never;
    /** Not available for BackButton */
    outline?: never;
    /** Not available for BackButton */
    plain?: never;
};

/**
 * A plain {@link Button} with a back chevron icon
 */
export function BackButton(props: BackButtonProps) {
    const { children, ...other } = props;

    return (
        <Button plain={true} {...other}>
            <ChevronLeftIcon />
            <Text>{children}</Text>
        </Button>
    );
}

/**
 * The properties for {@link PrimaryButton}
 */
export type PrimaryButtonProps = ButtonProps & {
    /** Not available for PrimaryButton */
    color?: never;
    /** Not available for PrimaryButton */
    outline?: never;
    /** Not available for PrimaryButton */
    plain?: never;
};

/**
 * A blue primary action {@link Button}
 */
export function PrimaryButton(props: PrimaryButtonProps) {
    return <Button color={"blue"} {...props} />;
}
